import type { QueryClient, QueryKey } from "@tanstack/react-query";

import { datasetQueryKeys } from "@/shared/domain/datasets/hooks";
import { eventQueryKeys } from "@/shared/domain/events/hooks";
import { fileQueryKeys } from "@/shared/domain/files/hooks";

import type {
  CollectionRecord,
  CollectionResourceType,
} from "../CollectionRecord";

import { collectionQueryKeys } from "./collectionQueryKeys";

/**
 * Cache maintenance after a collection write.
 *
 * Shared by the TanStack mutation hooks and by the AI client tools, which
 * perform the same writes outside any mutation. Keeping one copy is what
 * stops the two paths from invalidating different things for the same write.
 *
 * Create and membership-change are deliberately *separate* contracts rather
 * than one "collection was mutated" hook: a newly created collection has no
 * change history to refetch, so folding them together would make every create
 * invalidate a query that cannot have stale data.
 */

/**
 * Query key of the resource list that embeds a collection's membership, keyed
 * by the type the collection holds.
 *
 * A `Record` over the union, so a fourth `CollectionResourceType` is a compile
 * error here. A lookup rather than a `switch` because `resource_type` arrives
 * through an unchecked response cast: an unrecognized value yields `undefined`
 * instead of falling off the end of an exhaustive switch.
 */
const MEMBERSHIP_QUERY_KEYS: Record<
  CollectionResourceType,
  (record: CollectionRecord) => QueryKey
> = {
  dataset: () => datasetQueryKeys.lists(),
  file: (record) => fileQueryKeys.lists(record.org_id),
  event: (record) =>
    eventQueryKeys.listsScope(record.org_id, {
      type: "collection",
      id: record.collection_id,
    }),
};

/**
 * Seed the detail cache from a write's response and refresh the org's
 * collection list.
 *
 * The response is the newest state of the record, so it is written straight
 * into the detail cache rather than being invalidated and refetched.
 */
export function onCollectionCreated(
  queryClient: QueryClient,
  record: CollectionRecord,
): void {
  queryClient.setQueryData(
    collectionQueryKeys.detail(record.collection_id),
    record,
  );
  void queryClient.invalidateQueries({
    queryKey: collectionQueryKeys.lists(record.org_id),
  });
}

/**
 * As {@link onCollectionCreated}, plus the views that only a membership change
 * can invalidate: the collection's change history, and the resource list of
 * whichever type the collection holds.
 */
export function onCollectionMembersChanged(
  queryClient: QueryClient,
  record: CollectionRecord,
): void {
  onCollectionCreated(queryClient, record);
  void queryClient.invalidateQueries({
    queryKey: collectionQueryKeys.changes(record.collection_id),
  });
  // Guarded because `invalidateQueries` with an undefined key skips key
  // matching altogether and invalidates every query in the cache.
  const membershipKey = MEMBERSHIP_QUERY_KEYS[record.resource_type]?.(record);
  if (membershipKey) {
    void queryClient.invalidateQueries({ queryKey: membershipKey });
  }
}
